import { Status, TableColumns, TextLink } from '@teamsnap/snap-ui';
import { TEAMSNAP_URL } from 'core/constants';
import { currencyGivenPennies } from 'core/utils';
import { DateFormat, DateService } from 'frontend-toolkit';
import { AMPLITUDE_FINANCIAL_ACTIONS } from 'hooks/amplitude';
import * as React from 'react';

// Call the action callback, then navigate to the href
const onAction = (
  href: string,
  action: AMPLITUDE_FINANCIAL_ACTIONS,
  callback: (action: AMPLITUDE_FINANCIAL_ACTIONS) => void
) => {
  callback(action);
  window.open(href, '_blank', 'noopener,noreferrer');
};

export const getColumns = (
  actionCallback: (action: AMPLITUDE_FINANCIAL_ACTIONS) => void
): TableColumns => ({
  dueDate: {
    label: 'Due Date',
    sort: 'date',
    align: 'left',
    getBodyProps: (row) => {
      const [paymentYear, paymentMonth, paymentDay] = row.dueDate
        ? new Date(row.dueDate).toISOString().split('T')[0].split('-')
        : [];
      return {
        type: 'custom',
        height: 'tall',
        children: row.dueDate
          ? DateService.format(
              new Date(+paymentYear, +paymentMonth - 1, +paymentDay),
              DateFormat.DD_MM_YYYY
            )
          : EMPTY_VALUE(),
      };
    },
    width: '120px',
  },
  programName: {
    label: 'Program',
    sort: 'alpha',
    align: 'left',
    width: '200px',
  },
  source: {
    label: 'Source',
    sort: 'alpha',
    align: 'left',
    getBodyProps: (row) => {
      const sourceItem = row.sourceItems[0];
      if (!sourceItem) {
        return {
          type: 'custom',
          children: EMPTY_VALUE(),
        };
      }
      return {
        type: 'custom',
        children: sourceItem.sourceName,
      };
    },
    width: '200px',
  },
  orderType: {
    label: 'Source Type',
    sort: 'alpha',
    align: 'left',
    width: '120px',
  },
  member: {
    label: 'Member',
    sort: 'alpha',
    align: 'left',
    width: '200px',
  },
  status: {
    label: 'Status',
    sort: 'alpha',
    align: 'left',
    getBodyProps: (row) => ({
      type: 'custom',
      children: statusComponents[row.status],
    }),
    width: '200px',
  },
  amountDue: {
    label: 'Amount Due',
    sort: 'numeric',
    align: 'right',
    getBodyProps: (row) => ({
      type: 'custom',
      children: currencyGivenPennies(row.amountDue),
    }),
    width: '180px',
  },
  action: {
    label: 'Actions',
    align: 'left',
    width: '240px',
    getBodyProps: (row) => {
      const sourceItem = row.sourceItems[0];
      if (!sourceItem) {
        return {
          type: 'custom',
          children: EMPTY_VALUE(),
        };
      }

      if (row.orderType === 'Registration') {
        const viewOrderLink = row.orderNumber
          ? `/organizations/${sourceItem.linkInfo.organizationId}/financials/order/${row.orderNumber}/details`
          : `${TEAMSNAP_URL}/divisions/${sourceItem.linkInfo.rootDivisionId}/registration/${sourceItem.linkInfo.registrationFormId}/fees_and_payments_overview?registration_id=${sourceItem.linkInfo.registrationId}`;

        const viewRegistrationLink = row.orderNumber
          ? `/organizations/${sourceItem.linkInfo.organizationId}/registration/${sourceItem.linkInfo.registrationFormId}/details`
          : `${TEAMSNAP_URL}/divisions/${sourceItem.linkInfo.rootDivisionId}/registration/${sourceItem.linkInfo.registrationFormId}`;

        return {
          type: 'custom',
          children: (
            <div className="sui-flex sui-gap-3">
              <TextLink
                variantType="primary"
                onClick={() =>
                  onAction(
                    viewOrderLink,
                    AMPLITUDE_FINANCIAL_ACTIONS.VIEW_ORDER,
                    actionCallback
                  )
                }
                href={'#'}
                className="sui-whitespace-nowrap"
              >
                View Order
              </TextLink>
              <TextLink
                variantType="primary"
                onClick={() =>
                  onAction(
                    viewRegistrationLink,
                    AMPLITUDE_FINANCIAL_ACTIONS.VIEW_REGISTRATION,
                    actionCallback
                  )
                }
                href={'#'}
                className="sui-whitespace-nowrap"
              >
                View Registration
              </TextLink>
            </div>
          ),
        };
      }

      const viewInvoiceLink = `${TEAMSNAP_URL}/${sourceItem.linkInfo.rootDivisionId}/league_payments/invoicing/${sourceItem.linkInfo.batchInvoiceId}/invoices/${sourceItem.linkInfo.invoiceId}`;
      const viewBatchLink = `${TEAMSNAP_URL}/${sourceItem.linkInfo.rootDivisionId}/league_payments/invoicing/${sourceItem.linkInfo.batchInvoiceId}/invoices`;

      return {
        type: 'custom',
        children: (
          <div className="sui-flex sui-gap-3">
            <TextLink
              variantType="primary"
              onClick={() =>
                onAction(
                  viewInvoiceLink,
                  AMPLITUDE_FINANCIAL_ACTIONS.VIEW_INVOICE,
                  actionCallback
                )
              }
              href={'#'}
              className="sui-whitespace-nowrap"
            >
              View Invoice
            </TextLink>
            <TextLink
              variantType="primary"
              onClick={() =>
                onAction(
                  viewBatchLink,
                  AMPLITUDE_FINANCIAL_ACTIONS.VIEW_BATCH,
                  actionCallback
                )
              }
              href={'#'}
              className="sui-whitespace-nowrap"
            >
              View Batch
            </TextLink>
          </div>
        ),
      };
    },
  },
});

const statusComponents = {
  Due: <Status state="warning" text="Due" />,
  'Past Due': <Status state="negative" text="Past Due" />,
  'Pending Cash Payment': <Status state="info" text="Pending Cash Payment" />,
  Scheduled: <Status state="success" text="Scheduled" />,
};

const EMPTY_VALUE = () => <div>&#8212;</div>;

export const CSV_HEADERS = [
  'Date',
  'Program',
  'Source',
  'Source Type',
  'Member',
  'Status',
  'Amount Due',
];
